import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Zap,
  ShieldCheck,
  Truck,
  PencilRuler,
  MessageCircle,
  Star,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import useSEO from "../lib/useSEO";
import {
  fetchCategories,
  fetchProducts,
  fetchTestimonials,
} from "../lib/api";
import { BRAND, buildWhatsAppLink } from "../lib/constants";

const HERO_IMG =
  "/hero-image-1.jpg";

const PORTFOLIO = [
  "https://images.unsplash.com/photo-1768084356884-22bb77e76931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzdHJlZXR3ZWFyJTIwbW9kZWwlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI4MHww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1760174012435-630a17a434ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmdXRzYWwlMjBwbGF5ZXIlMjBhY3Rpb258ZW58MHx8fHwxNzc3NjE3MjgwfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1564115547164-8acb1a158428?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxqYXBhbmVzZSUyMHN0cmVldHdlYXIlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI5Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1767036841778-294c81791933?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxyYWNpbmclMjBqYWNrZXQlMjBkYXJrfGVufDB8fHx8MTc3NzYxNzI5Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1771873679764-4e5503b69040?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMHRleHR1cmV8ZW58MHx8fHwxNzc3NjE3Mjk2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/9404662/pexels-photo-9404662.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const USPS = [
  {
    icon: PencilRuler,
    title: "Bebas Custom",
    desc: "Logo, nama, nomor, sponsor, motif anime — semuanya bisa kami eksekusi tanpa minimum order.",
  },
  {
    icon: Zap,
    title: "Produksi Cepat",
    desc: "Standar 5–7 hari kerja. Tersedia opsi express untuk tim yang dikejar deadline turnamen.",
  },
  {
    icon: ShieldCheck,
    title: "Sublimasi Premium",
    desc: "Tinta menyerap ke serat kain. Tidak luntur, tidak retak, tidak mengelupas seperti DTF biasa.",
  },
  {
    icon: Truck,
    title: "Kirim Seluruh Indonesia",
    desc: "Pengiriman via JNE, J&T, SiCepat, dan Indah Cargo dari workshop kami di Wonosobo.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Brief & Desain",
    desc: "Chat admin di WhatsApp. Sharing referensi, logo, dan timeline tim kamu.",
  },
  {
    n: "02",
    title: "Mock-up & ACC",
    desc: "Tim desain kirim mock-up dalam 1–2 hari. Gratis 2x revisi sampai cocok.",
  },
  {
    n: "03",
    title: "Produksi & Kirim",
    desc: "Setelah ACC, produksi 5–7 hari kerja. Kami kirim ke seluruh Indonesia.",
  },
];

const FAQS = [
  {
    q: "Berapa minimum order custom apparel di Ace Sublimasi Nusantara?",
    a: "Tidak ada minimum order. Kami bisa terima pesanan satuan (1 pcs) maupun tim besar puluhan pcs untuk semua kategori produk.",
  },
  {
    q: "Berapa lama produksi jersey, jaket, atau apparel custom?",
    a: "Produksi standar 5–7 hari kerja setelah desain di-ACC. Untuk kebutuhan urgent, tersedia opsi express dengan biaya tambahan.",
  },
  {
    q: "Apakah print sublimasi tahan dicuci berkali-kali?",
    a: "Ya. Berbeda dengan DTF, tinta sublimasi menyerap ke serat kain, jadi tidak akan luntur, retak, atau mengelupas walau dicuci ratusan kali.",
  },
  {
    q: "Apa saja kategori produk yang bisa di-custom?",
    a: "Jersey futsal, jaket anime, haori Jepang, racing apparel (jersey & jacket), crop top, dan streetwear (T-shirt oversized, hoodie sublimasi).",
  },
  {
    q: "Bisa pesan untuk daerah luar Wonosobo / luar Jawa Tengah?",
    a: "Bisa. Workshop kami di Wonosobo, Jawa Tengah, dan kami melayani pengiriman ke seluruh Indonesia dengan ekspedisi pilihanmu.",
  },
  {
    q: "Bagaimana cara mulai pesan?",
    a: "Cukup chat admin di WhatsApp 0899 5482 862. Sharing referensi atau brief, dan tim desain kami akan bantu eksekusi dari 0.",
  },
];

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
    fetchProducts().then((p) => setFeatured(p.slice(0, 6))).catch(() => {});
    fetchTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  useSEO({
    title:
      "Ace Sublimasi Nusantara — Jersey Futsal, Jaket Anime, Haori, Racing & Streetwear Custom Indonesia",
    description:
      "Jasa sublimasi jersey, jaket anime, haori Jepang, racing apparel, dan streetwear custom dari Wonosobo. Bebas desain, tanpa minimum order, produksi 5–7 hari, kirim seluruh Indonesia.",
    keywords:
      "jersey futsal custom, jasa sublimasi jersey indonesia, jaket anime custom, haori jepang custom, jersey printing indonesia, racing apparel, streetwear custom",
    canonical: window.location.origin + "/",
    image: HERO_IMG,
    jsonLd: {
      id: "home-jsonld",
      data: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: BRAND.name,
        description: BRAND.description,
        url: window.location.origin,
        telephone: "+62" + BRAND.whatsapp.slice(2),
        address: {
          "@type": "PostalAddress",
          streetAddress: BRAND.address.line1 + ", " + BRAND.address.line2,
          addressLocality: BRAND.address.city,
          addressRegion: BRAND.address.province,
          postalCode: BRAND.address.postal,
          addressCountry: "ID",
        },
        sameAs: [
          `https://instagram.com/${BRAND.ig}`,
          `https://tiktok.com/@${BRAND.tiktok}`,
          `https://wa.me/${BRAND.whatsapp}`,
        ],
      },
    },
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center overflow-hidden border-b border-[#1f1f1f]"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Streetwear hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 grain-texture opacity-60 pointer-events-none" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24 md:py-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-8 lg:col-span-7 animate-rise">
            <div className="inline-flex items-center gap-2 border border-[#CCFF00]/40 px-3 py-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#CCFF00]" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold">
                Custom apparel sublimasi premium
              </span>
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase tracking-tight leading-[0.9] text-white"
            >
              Custom Your <br />
              <span className="text-[#CCFF00]">Identity.</span> <br />
              <span className="text-outline">Wear It Loud.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#C2C2C2] max-w-xl leading-relaxed">
              Jersey futsal, jaket anime, haori Jepang, racing apparel & streetwear —
              dibuat satu per satu dengan teknik sublimasi premium di workshop kami di Wonosobo.
              Bebas desain, tanpa minimum order, produksi 5–7 hari.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={buildWhatsAppLink(
                  "Halo Admin Ace Sublimasi Nusantara, saya mau custom apparel. Boleh konsultasi?"
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-cta"
                className="btn-primary"
              >
                <MessageCircle className="w-5 h-5" />
                Custom Sekarang
              </a>
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="btn-ghost"
              >
                Lihat Katalog
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: "500+", l: "Tim & Customer" },
                { n: "5–7", l: "Hari Produksi" },
                { n: "100%", l: "Sublimasi" },
              ].map((s) => (
                <div key={s.l} className="border-l border-[#262626] pl-4">
                  <p className="font-display text-3xl sm:text-4xl text-[#CCFF00] leading-none">
                    {s.n}
                  </p>
                  <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Side card */}
          <div className="hidden md:flex md:col-span-4 lg:col-span-5 items-end justify-end">
            <div className="border border-[#262626] bg-[#0a0a0a]/80 backdrop-blur-sm p-6 max-w-sm">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold">
                Featured
              </p>
              <h3 className="font-display text-2xl uppercase mt-2 text-white">
                Tim futsal kamu butuh seragam baru?
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-3">
                Konsultasi gratis 1-on-1 dengan tim desain kami. Kirim brief, terima mock-up dalam 24 jam.
              </p>
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-side-cta"
                className="mt-5 inline-flex items-center gap-2 text-[#CCFF00] font-bold text-sm uppercase tracking-widest border-b border-[#CCFF00] pb-1 hover:gap-3 transition-all"
              >
                Konsultasi Gratis <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE USPs */}
      <Marquee
        items={[
          "FAST PRODUCTION 5–7 DAYS",
          "PREMIUM SUBLIMATION",
          "NO MINIMUM ORDER",
          "FREE DESIGN CONSULTATION",
          "SHIP NATIONWIDE",
        ]}
      />

      {/* CATEGORIES */}
      <section
        data-testid="categories-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-6">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Kategori Produk
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
                Pilih Lapanganmu, <br />
                <span className="text-outline">Kami Bikin Apparelnya.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-base text-[#A3A3A3]">
                Lima vertikal yang sudah kami kuasai. Dari taklukan lapangan futsal sampai
                tampil all-out di bazar anime, semuanya bisa di-custom.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[180px] md:auto-rows-[220px]">
            {categories.map((c, idx) => {
              const positions = [
                "col-span-2 md:col-span-7 md:row-span-2 row-span-2",
                "col-span-2 md:col-span-5 md:row-span-1",
                "col-span-1 md:col-span-3 md:row-span-1",
                "col-span-1 md:col-span-3 md:row-span-1",
                "col-span-2 md:col-span-6 md:row-span-1",
              ];
              return (
                <Link
                  key={c.slug}
                  to={`/shop?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className={`relative group overflow-hidden border border-[#262626] hover:border-[#CCFF00] transition-colors ${positions[idx % positions.length]}`}
                >
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover grayscale-[40%] opacity-90 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                  <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#CCFF00] font-bold">
                        / {String(idx + 1).padStart(2, "0")}
                      </span>
                      <ArrowUpRight className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl lg:text-4xl uppercase text-white leading-none tracking-tight">
                        {c.name}
                      </h3>
                      <p className="hidden md:block text-sm text-[#C2C2C2] mt-2 max-w-xs">
                        {c.tagline}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* USPs */}
      <section
        data-testid="usp-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f] bg-[#070707]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-12 items-end">
            <div className="md:col-span-7">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Kenapa Ace Sublimasi Nusantara
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
                Bukan sekadar <br />
                <span className="text-[#CCFF00]">jasa printing.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="text-base text-[#A3A3A3]">
                Kami partner kreatif yang ngerti tim, komunitas, dan brand kamu. Bukan
                sekadar mesin produksi.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#262626] divide-y md:divide-y-0 md:divide-x divide-[#262626]">
            {USPS.map((u, idx) => (
              <div
                key={u.title}
                data-testid={`usp-card-${idx}`}
                className="p-6 md:p-8 group hover:bg-[#0e0e0e] transition-colors"
              >
                <div className="w-12 h-12 border border-[#262626] inline-flex items-center justify-center text-[#CCFF00] group-hover:border-[#CCFF00] group-hover:bg-[#CCFF00] group-hover:text-black transition-all">
                  <u.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl uppercase mt-5 text-white tracking-tight">
                  {u.title}
                </h3>
                <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">
                  {u.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section
        data-testid="featured-products-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Featured
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
                Best Seller <br />
                <span className="text-outline">Customer Picks</span>
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="featured-view-all"
              className="hidden md:inline-flex items-center gap-2 text-[#CCFF00] font-bold text-sm uppercase tracking-widest border-b border-[#CCFF00] pb-1 hover:gap-3 transition-all"
            >
              Lihat Semua <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>

          <div className="mt-10 md:hidden text-center">
            <Link
              to="/shop"
              data-testid="featured-view-all-mobile"
              className="btn-ghost"
            >
              Lihat Semua Produk <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section
        data-testid="portfolio-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-7">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Portfolio
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
                Project Terbaru <br />
                <span className="text-outline">Yang Sudah Kami Eksekusi.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-base text-[#A3A3A3]">
                Real shots dari workshop kami. Tim, komunitas, dan brand yang sudah percaya kami untuk eksekusi visi mereka.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4 auto-rows-[140px] md:auto-rows-[180px]">
            {[
              "col-span-2 md:col-span-5 md:row-span-2",
              "col-span-1 md:col-span-4 md:row-span-1",
              "col-span-1 md:col-span-3 md:row-span-1",
              "col-span-2 md:col-span-4 md:row-span-1",
              "col-span-1 md:col-span-3 md:row-span-1",
              "col-span-1 md:col-span-5 md:row-span-1",
            ].map((cls, idx) => (
              <div
                key={idx}
                data-testid={`portfolio-tile-${idx}`}
                className={`relative overflow-hidden border border-[#262626] group ${cls}`}
              >
                <img
                  src={PORTFOLIO[idx % PORTFOLIO.length]}
                  alt={`Portfolio ${idx + 1}`}
                  className="w-full h-full object-cover grayscale-[40%] opacity-90 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        data-testid="process-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f] bg-[#070707] relative overflow-hidden"
      >
        <div className="absolute inset-0 grain-texture opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
              / Proses Order
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
              3 Langkah Simpel <br />
              <span className="text-[#CCFF00]">Apparel Custom Datang.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#1a1a1a] border border-[#1a1a1a]">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="relative bg-[#050505] p-8 md:p-10 group hover:bg-[#0a0a0a] transition-colors"
              >
                <span className="font-display text-[120px] md:text-[160px] leading-none text-[#0f0f0f] absolute -top-2 right-4 select-none pointer-events-none group-hover:text-[#1a1a1a] transition-colors">
                  {s.n}
                </span>
                <div className="relative">
                  <p className="text-xs tracking-[0.3em] uppercase text-[#CCFF00] font-bold">
                    Step {s.n}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl uppercase mt-3 text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="process-whatsapp-cta"
              className="btn-primary"
            >
              <MessageCircle className="w-5 h-5" />
              Mulai Langkah 01
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-7">
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
                / Cerita Customer
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
                Mereka Sudah <br />
                <span className="text-outline">Pakai Apparel Kami.</span>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t) => (
              <div
                key={t.id}
                data-testid={`testimonial-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="border border-[#262626] p-6 md:p-7 bg-[#0a0a0a] hover:border-[#CCFF00] transition-colors flex flex-col"
              >
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-[#CCFF00] text-[#CCFF00]"
                    />
                  ))}
                </div>
                <p className="mt-4 text-base text-white leading-relaxed">
                  “{t.quote}”
                </p>
                <div className="mt-6 pt-6 border-t border-[#262626]">
                  <p className="font-display text-lg uppercase text-white tracking-tight">
                    {t.name}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-1">
                    {t.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="faq-section"
        className="py-20 sm:py-28 border-b border-[#1f1f1f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
              / FAQ
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
              Pertanyaan <br />
              <span className="text-outline">Yang Sering Datang.</span>
            </h2>
            <p className="mt-6 text-base text-[#A3A3A3]">
              Belum nemu jawabannya? Chat admin di WhatsApp, kami balas ramah dalam menit.
            </p>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="faq-whatsapp-cta"
              className="mt-8 btn-primary inline-flex"
            >
              <MessageCircle className="w-5 h-5" />
              Tanya Admin
            </a>
          </div>
          <div className="md:col-span-7">
            <Accordion
              type="single"
              collapsible
              className="border border-[#262626]"
              data-testid="faq-accordion"
            >
              {FAQS.map((f, idx) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${idx}`}
                  className="border-b border-[#262626] last:border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${idx}`}
                    className="px-5 md:px-6 py-5 text-left text-base md:text-lg text-white font-bold uppercase tracking-tight hover:text-[#CCFF00] hover:no-underline data-[state=open]:text-[#CCFF00]"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 md:px-6 pb-5 text-sm md:text-base text-[#C2C2C2] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="relative py-24 sm:py-32 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1771873679764-4e5503b69040?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmVvbiUyMHRleHR1cmV8ZW58MHx8fHwxNzc3NjE3Mjk2fDA&ixlib=rb-4.1.0&q=85"
            alt="texture"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Ready to roll?
          </p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] text-white max-w-5xl mx-auto">
            Stop Bayangin <span className="text-[#CCFF00]">Apparelnya.</span> <br />
            Mulai <span className="text-outline">Pakainya.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#C2C2C2] max-w-2xl mx-auto">
            Kirim brief tim kamu sekarang, dapat mock-up dalam 24 jam, dan apparel custom siap dipakai dalam seminggu.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="cta-final-whatsapp"
              className="btn-primary"
            >
              <MessageCircle className="w-5 h-5" />
              Chat Admin Sekarang
            </a>
            <Link
              to="/custom"
              data-testid="cta-final-custom"
              className="btn-ghost"
            >
              Pelajari Cara Order
            </Link>
          </div>

          <div className="mt-14 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-[#A3A3A3]">
            {[
              "Tanpa minimum order",
              "Gratis 2x revisi desain",
              "Produksi 5–7 hari",
              "Kirim seluruh Indonesia",
            ].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#CCFF00]" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
